import type React from 'react';
import type { ComponentProps } from 'react';
import type { TableEmptyType } from '../table-empty';

export const CHECKBOX_COLUMN_WIDTH_PX = 48;
/** Skeleton rows drawn while loading when the caller gives no row count. */
export const DEFAULT_SKELETON_ROW_COUNT = 5;

/**
 * Placeholder width when a column gives no `skeletonWidthCh`. Twelve
 * characters is about what a short label or a date runs to — wide enough
 * that the column does not collapse to its header, narrow enough that it
 * rarely has to shrink.
 */
export const DEFAULT_SKELETON_WIDTH_CH = 12;

/** How a table fills its body while data is in flight. */
export enum TableLoadingVariant {
	/** Placeholder rows shaped like the loaded table. Preserves height. */
	Skeleton = 'skeleton',
	/** A single centred spinner. Collapses the body to one status row. */
	Spinner = 'spinner',
}

export const ACTIONS_COLUMN_ID = 'actions';

export type ITableProps = ComponentProps<'table'> & {
	containerRef?: React.RefObject<HTMLDivElement | null>;
};

export type ITableHeaderProps = ComponentProps<'thead'> & {
	hasToolbar?: boolean;
};
export type ITableBodyProps = ComponentProps<'tbody'> & {
	isEmpty?: boolean;
	emptyType?: TableEmptyType;
	emptyMessage?: string;
	emptyColSpan?: number;
	emptyMinHeightPx?: number;
	emptyTestId?: string;
	isLoading?: boolean;
	loadingVariant?: TableLoadingVariant;
	/** Skeleton rows to draw. Pass the page size to avoid a jump. */
	skeletonRowCount?: number;
	/**
	 * Columns to shape the skeleton from. Without them this low-level body
	 * has no idea what the table looks like and falls back to the spinner —
	 * `TableBodySection` passes its own columns through.
	 */
	skeletonColumns?: ITableSectionColumn[];
	hasCheckboxColumn?: boolean;
	/** Announced once while loading, instead of reading every placeholder. */
	loadingLabel?: string;
	loadingMinHeightPx?: number;
	loadingTestId?: string;
};
export type ITableFooterProps = ComponentProps<'tfoot'>;
export type ITableRowProps = ComponentProps<'tr'> & {
	isSelected?: boolean;
	isRowHoverEnabled?: boolean;
	isHoverDisabled?: boolean;
};
export type ITableHeadProps = ComponentProps<'th'> & {
	align?: 'left' | 'center' | 'right';
	sticky?: 'left' | 'right';
	stickyLeftOffset?: number;
};
export type ITableCellProps = ComponentProps<'td'> & {
	align?: 'left' | 'center' | 'right';
	sticky?: 'left' | 'right';
	stickyLeftOffset?: number;
};
export type ITableCaptionProps = ComponentProps<'caption'>;

export interface ITableToolbarProps {
	children: React.ReactNode;
	className?: string;
	/**
	 * Dims the toolbar and stops it taking input, for use while the table
	 * loads. It stays mounted so the layout holds still.
	 */
	isDisabled?: boolean;
}

export enum OrderBy {
	ASC = '',
	DESC = '-',
}

export type ITableSortState = {
	field: string;
	order: OrderBy;
};

export function tableSortToQueryParam(sort: ITableSortState): string {
	return `${sort.order}${sort.field}`;
}

export function tableSortFromQueryParam(sort: string): ITableSortState {
	if (!sort) {
		return { field: '', order: OrderBy.ASC };
	}

	if (sort.startsWith('-')) {
		return { field: sort.slice(1), order: OrderBy.DESC };
	}

	return { field: sort, order: OrderBy.ASC };
}

export interface ITableSectionColumn {
	id: string;
	name: string;
	field?: string;
	isSortable?: boolean;
	sticky?: 'left' | 'right';
	align?: 'left' | 'center' | 'right';
	/**
	 * Roughly how many characters this column's values run to, used to size
	 * its loading placeholder. The table sizes columns from their content,
	 * so a placeholder that is narrower than the data makes the column grow
	 * when the data lands. Set this to the typical value length (an email
	 * column, say, around 24) and the column barely moves.
	 */
	skeletonWidthCh?: number;
}

export interface ITableActionItem {
	label: string;
	onClick: () => void;
}

export interface ITablePaginationProps {
	currentPage: number;
	totalItems: number;
	itemsPerPage: number;
	onPageChange: (page: number) => void;
	onItemsPerPageChange?: (itemsPerPage: number) => void;
	itemsPerPageOptions?: number[];
	labelShowing?: string;
	labelItemsPerPage?: string;
	className?: string;
	shouldHidePerPage?: boolean;
	shouldHideSummary?: boolean;
	/** Stops paging while the table loads, without unmounting the controls. */
	isDisabled?: boolean;
}
